// Apolo IDE — ContextTabView (sprint context-tab).
//
// The header "Contexto" tab, opened from the composer's "Add context" button.
// Layout mirrors a knowledge/wiki panel (image 1): a left rail listing the
// staged context items + a main preview pane. It accepts ANY file type
// (md/text rendered, images shown, video/audio playable, binary as a card) via
// the shared useFileAttachments store (drag-drop + picker + paste).
//
// Two destinations for the staged context (as Pedro asked):
//   1. CONHECIMENTO (self-improvement, cross-session): "Salvar no conhecimento"
//      persists a text/markdown item to code_user_memories — the same store the
//      agent injects every turn (the "right place", not session-scoped).
//   2. CHAT principal: the reused composer below (SessionComposer in the dock)
//      sends turns to the same session, so anything typed/attached there feeds
//      the active chat.
//
// PRESENTACIONAL + 1 efeito de I/O (POST memory). Recebe o store de anexos por
// prop (lifted no SessionWorkspace) pra sobreviver à troca de aba.

import { useEffect, useMemo, useState } from 'react';
import {
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  File as FileIcon,
  Upload,
  BookOpenCheck,
  Check,
  Loader2,
  X,
} from 'lucide-react';
import MarkdownMessage from '../../MarkdownMessage.jsx';
import { apiFetch } from '../../../lib/api.js';

const MD_RE = /\.(md|markdown|mdx)$/i;

function iconFor(kind, name) {
  if (kind === 'image') return ImageIcon;
  if (kind === 'text') return FileText;
  const n = (name || '').toLowerCase();
  if (/\.(mp4|webm|mov|m4v|avi|mkv)$/.test(n)) return Film;
  if (/\.(mp3|wav|ogg|m4a|flac|aac)$/.test(n)) return Music;
  return FileIcon;
}

function prettySize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Preview pane for a single staged item. Object URLs for media are created here
// and revoked on change/unmount (no leaks).
function FilePreview({ item }) {
  const mediaUrl = useMemo(() => {
    if (!item?.file) return null;
    const t = (item.type || '').toLowerCase();
    const n = (item.name || '').toLowerCase();
    const isMedia =
      t.startsWith('video/') ||
      t.startsWith('audio/') ||
      /\.(mp4|webm|mov|m4v|avi|mkv|mp3|wav|ogg|m4a|flac|aac)$/.test(n);
    if (!isMedia) return null;
    try {
      return URL.createObjectURL(item.file);
    } catch {
      return null;
    }
  }, [item?.file, item?.type, item?.name]);

  useEffect(() => () => {
    if (mediaUrl) URL.revokeObjectURL(mediaUrl);
  }, [mediaUrl]);

  if (!item) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-apolo-textTertiary"
        data-testid="context-empty"
      >
        <Upload size={28} aria-hidden="true" className="opacity-60" />
        <p className="text-[13px]">Arraste arquivos aqui ou clique em “Adicionar arquivos”.</p>
        <p className="max-w-sm text-[11px] opacity-80">
          Aceita markdown, imagens, vídeos e qualquer tipo de arquivo. Texto e
          markdown podem virar conhecimento permanente; tudo pode alimentar o chat.
        </p>
      </div>
    );
  }

  const isImage = item.kind === 'image' && item.preview;
  const isMd = item.kind === 'text' && MD_RE.test(item.name || '');
  const isText = item.kind === 'text';
  const t = (item.type || '').toLowerCase();
  const isVideo = mediaUrl && (t.startsWith('video/') || /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(item.name || ''));
  const isAudio = mediaUrl && (t.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|flac|aac)$/i.test(item.name || ''));

  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="context-preview">
      {isImage ? (
        <img
          src={item.preview}
          alt={item.name}
          className="mx-auto max-h-full max-w-full rounded-lg border border-apolo-borderSubtle object-contain"
        />
      ) : isMd ? (
        <div className="rx-shell">
          <MarkdownMessage content={item.textContent || ''} />
        </div>
      ) : isVideo ? (
        <video src={mediaUrl} controls className="mx-auto max-h-full max-w-full rounded-lg">
          <track kind="captions" />
        </video>
      ) : isAudio ? (
        <audio src={mediaUrl} controls className="w-full">
          <track kind="captions" />
        </audio>
      ) : isText ? (
        <pre className="whitespace-pre-wrap break-words rounded-lg border border-apolo-borderSubtle bg-apolo-surface1 p-3 font-mono text-[12px] text-apolo-textSecondary">
          {item.textContent || ''}
        </pre>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-apolo-borderSubtle bg-apolo-surface1 p-8 text-center text-apolo-textTertiary">
          <FileIcon size={26} aria-hidden="true" />
          <p className="text-[13px] text-apolo-textSecondary">{item.name}</p>
          <p className="text-[11px]">
            {prettySize(item.size)}{item.type ? ` · ${item.type}` : ''}
          </p>
          <p className="text-[11px] opacity-80">Pré-visualização não disponível para este tipo.</p>
        </div>
      )}
    </div>
  );
}

export default function ContextTabView({ contextFiles, sessionId }) {
  const {
    attachments,
    dragOver,
    uploadError,
    fileInputRef,
    removeAttachment,
    openFilePicker,
    dropZoneHandlers,
    fileInputHandlers,
  } = contextFiles;

  const [selectedId, setSelectedId] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveMsg, setSaveMsg] = useState(null);

  // Keep a valid selection as items come and go.
  const selected =
    attachments.find((a) => a.id === selectedId) || attachments[0] || null;
  useEffect(() => {
    if (!selected && selectedId) setSelectedId(null);
    else if (selected && selected.id !== selectedId) setSelectedId(selected.id);
  }, [selected, selectedId]);

  const canSaveKnowledge = selected?.kind === 'text' && !!selected?.textContent;

  async function saveToKnowledge() {
    if (!canSaveKnowledge || saveState === 'saving') return;
    setSaveState('saving');
    setSaveMsg(null);
    // The same write seam MemoryEditor uses: code_user_memories (cross-session,
    // injected every turn). We prefix the file name so the memory reads cleanly.
    const content = `# ${selected.name}\n\n${selected.textContent}`.slice(0, 30000);
    const res = await apiFetch('/api/code-ide/memory', {
      method: 'POST',
      body: JSON.stringify({ content, source_session_id: sessionId || undefined }),
    });
    if (res.ok) {
      setSaveState('saved');
      setSaveMsg('Salvo no conhecimento — o agente verá isto em toda sessão.');
      setTimeout(() => setSaveState('idle'), 2200);
    } else {
      setSaveState('error');
      setSaveMsg(res.error || 'Falha ao salvar no conhecimento.');
    }
  }

  return (
    <div
      className="relative flex min-h-0 flex-1"
      data-testid="context-tab-view"
      aria-label="Contexto"
      {...dropZoneHandlers}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        {...fileInputHandlers}
        className="hidden"
        aria-hidden="true"
      />

      {/* Left rail — staged context items (image-1 style nav). */}
      <aside className="flex w-[230px] shrink-0 flex-col border-r border-apolo-borderSubtle bg-apolo-surface1/40">
        <div className="flex items-center justify-between px-3 py-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-apolo-textTertiary">
            Contexto{attachments.length ? ` · ${attachments.length}` : ''}
          </span>
          <button
            type="button"
            onClick={openFilePicker}
            title="Adicionar arquivos (qualquer tipo)"
            aria-label="Adicionar arquivos"
            className="inline-flex h-6 items-center gap-1 rounded-full border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-[11px] text-apolo-textSecondary transition-colors hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
          >
            <Upload size={11} aria-hidden="true" /> Adicionar
          </button>
        </div>
        <ul className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pb-2">
          {attachments.length === 0 ? (
            <li className="px-2 py-3 text-[11px] text-apolo-textTertiary">
              Nenhum arquivo ainda.
            </li>
          ) : (
            attachments.map((a) => {
              const Icon = iconFor(a.kind, a.name);
              const active = a.id === selected?.id;
              return (
                <li key={a.id}>
                  <div
                    className={`group flex items-center gap-2 rounded-md px-2 py-1.5 text-[12px] transition ${
                      active
                        ? 'bg-apolo-surface2 text-apolo-textPrimary'
                        : 'text-apolo-textSecondary hover:bg-apolo-surface2/60'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedId(a.id)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      aria-current={active ? 'true' : undefined}
                    >
                      <Icon size={13} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
                      <span className="truncate">{a.name}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.id)}
                      aria-label={`Remover ${a.name}`}
                      title="Remover"
                      className="shrink-0 text-apolo-textTertiary opacity-0 transition hover:text-apolo-error group-hover:opacity-100"
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
            })
          )}
        </ul>
        {uploadError && (
          <p role="alert" className="px-3 py-2 text-[11px] text-apolo-error">{uploadError}</p>
        )}
      </aside>

      {/* Main — preview + knowledge action. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between gap-2 border-b border-apolo-borderSubtle px-4 py-2.5">
          <h2 className="min-w-0 truncate text-[14px] font-semibold text-apolo-textPrimary">
            {selected ? selected.name : 'Contexto'}
          </h2>
          {selected ? (
            <button
              type="button"
              onClick={saveToKnowledge}
              disabled={!canSaveKnowledge || saveState === 'saving'}
              title={
                canSaveKnowledge
                  ? 'Salvar este conteúdo no conhecimento permanente (o agente vê em toda sessão)'
                  : 'Só arquivos de texto/markdown viram conhecimento permanente'
              }
              data-testid="context-save-knowledge"
              className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-apolo-borderSubtle bg-apolo-surface2 px-3 text-[11px] font-medium text-apolo-textSecondary transition-colors hover:bg-apolo-surface3 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saveState === 'saving' ? (
                <Loader2 size={12} aria-hidden="true" className="animate-spin" />
              ) : saveState === 'saved' ? (
                <Check size={12} aria-hidden="true" className="text-apolo-success" />
              ) : (
                <BookOpenCheck size={12} aria-hidden="true" />
              )}
              {saveState === 'saved' ? 'Salvo' : 'Salvar no conhecimento'}
            </button>
          ) : null}
        </div>
        {saveMsg && (
          <p
            role="status"
            className={`px-4 py-1.5 text-[11px] ${saveState === 'error' ? 'text-apolo-error' : 'text-apolo-success'}`}
          >
            {saveMsg}
          </p>
        )}
        <div className="flex min-h-0 flex-1 flex-col p-4">
          <FilePreview item={selected} />
        </div>
      </div>

      {/* Drag overlay — the whole view accepts any file. */}
      {dragOver && (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-apolo-accent/60 bg-apolo-bg/70 text-[13px] font-medium text-apolo-textPrimary"
          aria-hidden="true"
        >
          <span className="inline-flex items-center gap-2">
            <Upload size={16} /> Solte para adicionar ao contexto
          </span>
        </div>
      )}
    </div>
  );
}
